import React from 'react';
import { PublicReportCarBodySectionDto } from '../types/models.types';
import { getRatingMessage } from '@/helpers/ratingState';
import { mapScoreToNumber } from './mapper-helpers';
import { RatingSection } from './mapper-types';
import {
  mapPaintThicknessAndGapMeasurements,
  MappedPaintAndGapMeasurements,
} from './paint-measurement.mapper';
import type {
  CarWindowsData,
  CarWindowsSubsection,
} from '@/components/GlassControl/GlassControl.types';
import type { TireData } from '@/components/TiresControl/TiresControl.types';
import type { CarWheelSubsectionDto, CarWheelDataDto, LightsSubsectionDto } from '../generatedClient';
import type { AssessmentItem } from '@/components/CarDetailsAssessment/CarDetailsAssessment.types';
import { BadgeStatusType } from '@/components/Badge/BadgeTypes';
import { Badge } from '@/components/Badge/Badge';

export interface MappedCarTiresSubsection {
  tires: TireData[];
  hasSecondSet: boolean;
  expertSummary: string | null;
}

export interface MappedCarLightsSubsection {
  assessmentItems: AssessmentItem[];
  usaLights: boolean;
  expertSummary: string | null;
}

export interface MappedCarBodySection {
  ratingSection: RatingSection;
  remarks: string[];
  paintAndGapMeasurements: MappedPaintAndGapMeasurements | null;
  windowsData: CarWindowsSubsection | null;
  tiresData: MappedCarTiresSubsection | null;
  lightsData: MappedCarLightsSubsection | null;
}

const mapTirePositionLabel = (position: 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight'): TireData['position'] => {
  const positionMap = {
    frontLeft: 'front-left',
    frontRight: 'front-right',
    rearLeft: 'rear-left',
    rearRight: 'rear-right',
  } as const;
  return positionMap[position];
};

const mapTireType = (apiType?: string): TireData['type'] => {
  if (!apiType) return 'Letnia';

  const typeMap: Record<string, TireData['type']> = {
    SUMMER: 'Letnia',
    WINTER: 'Zimowa',
    ALL_SEASON: 'Całoroczna',
  };

  return typeMap[apiType] || 'Letnia';
};

const mapRimType = (apiType?: 'STEEL' | 'ALLOY'): TireData['rimType'] => {
  if (apiType === 'ALLOY') return 'Aluminiowa';
  if (apiType === 'STEEL') return 'Stalowa';
  return 'Brak informacji';
};

const mapTireStatus = (apiStatus?: 'NO_ISSUES' | 'DAMAGED_EDGES' | 'MULTIPLE_DAMAGES'): TireData['rimStatus'] => {
  if (apiStatus === 'NO_ISSUES') return 'Bardzo dobry';
  if (apiStatus === 'DAMAGED_EDGES') return 'Uszkodzone krawędzie';
  if (apiStatus === 'MULTIPLE_DAMAGES') return 'Zły';
  return 'Brak informacji';
};

const mapReplacementStatus = (fieldValue?: string): TireData['replacementStatus'] => {
  if (!fieldValue) return null;
  if (fieldValue === 'NO_REPLACEMENT' || fieldValue === 'Nie') return 'Nie';
  if (fieldValue === 'LAST_SEASON' || fieldValue === 'Ostatni sezon') return 'Ostatni sezon';
  if (fieldValue === 'NEEDS_REPLACEMENT' || fieldValue === 'Tak') return 'Tak';
  return null;
};

const mapTreadConditionStatus = (apiStatus?: 'NO_ISSUES' | 'DAMAGED_EDGES' | 'MULTIPLE_DAMAGES'): TireData['rimConditionStatus'] => {
  if (!apiStatus) return null;
  if (apiStatus === 'NO_ISSUES') return 'OK';
  if (apiStatus === 'DAMAGED_EDGES') return 'Uszkodzone';
  if (apiStatus === 'MULTIPLE_DAMAGES') return 'Wielokrotne uszkodzenia';
  return null;
};

const mapSingleTire = (
  wheelData: CarWheelDataDto | undefined,
  position: 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight'
): TireData | null => {
  if (!wheelData) return null;

  // @ts-expect-error - API returns string instead of object with fieldSelectedValue
  const replacementValue: string | undefined = wheelData.replacementStatus;

  const needsReplacement = replacementValue === 'NEEDS_REPLACEMENT' || replacementValue === 'Tak';

  let replacementReason: TireData['replacementReason'];
  if (replacementValue === 'AGE') replacementReason = 'age';
  else if (replacementValue === 'WEAR') replacementReason = 'wear';
  else if (replacementValue === 'DAMAGE') replacementReason = 'damage';

  return {
    position: mapTirePositionLabel(position),
    brand: wheelData.manufacturer || 'Nieznana',
    model: wheelData.tradeName || 'Nieznany',
    size: wheelData.size || '',
    type: mapTireType(wheelData.tireType),
    productionDate: wheelData.productionDateCode || '',
    speedIndex: wheelData.speedIndex || '',
    treadDepth: {
      inner: parseFloat(wheelData.innerSide || '0') || 0,
      center: parseFloat(wheelData.center || '0') || 0,
      outer: parseFloat(wheelData.outerSide || '0') || 0,
    },
    rimType: mapRimType(wheelData.treadType),
    rimStatus: mapTireStatus(wheelData.treadConditionType),
    tireStatus: mapTireStatus(wheelData.treadConditionType),
    replacementStatus: mapReplacementStatus(replacementValue),
    rimConditionStatus: mapTreadConditionStatus(wheelData.treadConditionType),
    replacementReason,
    hasWarning: needsReplacement,
  };
};

export const mapCarTiresSubsection = (
  tiresSubsection: CarWheelSubsectionDto | null | undefined
): MappedCarTiresSubsection | null => {
  if (!tiresSubsection) return null;

  const tires: TireData[] = [];

  const frontLeft = mapSingleTire(tiresSubsection.frontLeft, 'frontLeft');
  const frontRight = mapSingleTire(tiresSubsection.frontRight, 'frontRight');
  const rearLeft = mapSingleTire(tiresSubsection.rearLeft, 'rearLeft');
  const rearRight = mapSingleTire(tiresSubsection.rearRight, 'rearRight');

  if (frontLeft) tires.push(frontLeft);
  if (frontRight) tires.push(frontRight);
  if (rearLeft) tires.push(rearLeft);
  if (rearRight) tires.push(rearRight);

  return {
    tires,
    hasSecondSet: tiresSubsection.sellerHasSecondSetOfWheels ?? false,
    expertSummary: tiresSubsection.expertSummary || null,
  };
};

const mapLightStatus = (status?: string): BadgeStatusType | undefined => {
  if (!status) return undefined;

  // Mapowanie statusów ze stringów API na typy BadgeStatus
  const statusMap: Record<string, BadgeStatusType> = {
    OK: BadgeStatusType.Success,
    SPRAWNE: BadgeStatusType.Success,
    NIESPRAWNE: BadgeStatusType.Error,
    INNE: BadgeStatusType.Neutral,
    BRAK: BadgeStatusType.Error,
  };

  return statusMap[status.toUpperCase()] || undefined;
};

const createLightItem = (
  id: string,
  title: string,
  iconName: AssessmentItem['iconUrl'],
  value?: string,
  remark?: string,
): AssessmentItem => {
  const status = mapLightStatus(value);
  const badgeText = value === 'OK' || value === 'SPRAWNE' ? 'OK' :
    value === 'NIESPRAWNE' ? 'NIESPRAWNE' :
      value === 'INNE' ? 'INNE' : value;

  return {
    id,
    iconUrl: iconName,
    title,
    status,
    badgeText,
    description: remark,
  };
};

const mapUsaHeadlightStatus = (value?: string): { status: BadgeStatusType; text: string } => {
  if (!value) return { status: BadgeStatusType.Neutral, text: 'Brak danych' };

  const statusMap: Record<string, { status: BadgeStatusType; text: string }> = {
    'Symetryczne': { status: BadgeStatusType.Error, text: 'SYMETRYCZNE' },
    'Asymetryczne': { status: BadgeStatusType.Success, text: 'ASYMETRYCZNE' },
    NO_DATA: { status: BadgeStatusType.Neutral, text: 'Brak danych' },
  };

  return statusMap[value];
};

const mapUsaRearFogStatus = (value?: string): { status: BadgeStatusType; text: string } => {
  if (!value) return { status: BadgeStatusType.Neutral, text: 'Brak danych' };

  const statusMap: Record<string, { status: BadgeStatusType; text: string }> = {
    'Ok': { status: BadgeStatusType.Success, text: 'OBECNE' },
    'Brak': { status: BadgeStatusType.Error, text: 'BRAK' },
    'Inne': { status: BadgeStatusType.Neutral, text: 'INNE' },
  };

  return statusMap[value];
};

const createUsaLightsCustomBody = (
  lightsSubsection: LightsSubsectionDto,
): React.ReactNode => {
  const rearFogStatus = mapUsaRearFogStatus(lightsSubsection.usaRearFogLight);
  const headlightStatus = mapUsaHeadlightStatus(lightsSubsection.usaFrontHeadLight);
  return (
    <div className="flex flex-col items-center px-4 pb-6">
      <div className="mt-5 mb-5 text-xl font-semibold leading-snug text-center text-zinc-800">
        Pojazdy z USA  / Kanady
      </div>
      <p className="text-[15px] font-medium text-center text-zinc-800">
        Obecność światła przeciwmgielnego tylnego
      </p>
      <div className="mt-3">
        <Badge variant={rearFogStatus.status} badgeText={rearFogStatus.text} version="v2" />
      </div>

      {lightsSubsection.usaRearFogLightRemark && (
        <p className="mt-4 text-sm text-center text-grayText">
          {lightsSubsection.usaRearFogLightRemark}
        </p>
      )}

      <p className="mt-6 text-[15px] font-medium text-center text-zinc-800">
        Reflektory przednie
      </p>
      <div className="mt-3">
        <Badge variant={headlightStatus.status} badgeText={headlightStatus.text} version="v2" />
      </div>

      {lightsSubsection.usaFrontHeadLightRemark && (
        <p className="mt-4 text-sm text-center text-grayText">
          {lightsSubsection.usaFrontHeadLightRemark}
        </p>
      )}
    </div>
  );
};

export const mapCarLightsSubsection = (
  lightsSubsection: LightsSubsectionDto | null | undefined
): MappedCarLightsSubsection | null => {
  if (!lightsSubsection) return null;

  const assessmentItems: AssessmentItem[] = [];

  // Standardowe światła - używam dostępnych ikon
  // UWAGA: pola brakeLight/reverseLight w API są zamienione względem etykiet w panelu admina
  const cols: Array<{ id: string; title: string; icon: AssessmentItem['iconUrl']; value?: string; remark?: string; status?: BadgeStatusType }> = [
    { id: 'lowBeam', title: 'Mijania', icon: 'light', value: lightsSubsection.lowBeamLight, remark: lightsSubsection.lowBeamLightRemark },
    { id: 'highBeam', title: 'Drogowe', icon: 'text-lines', value: lightsSubsection.highBeamLight, remark: lightsSubsection.highBeamLightRemark },
    { id: 'position', title: 'Pozycyjne', icon: 'wifi-signal', value: lightsSubsection.positionLight, remark: lightsSubsection.positionLightRemark },
    { id: 'fog', title: 'Przeciwmgłowe', icon: 'fingerprint', value: lightsSubsection.fogLight, remark: lightsSubsection.fogLightRemark },
    { id: 'reverse', title: 'Cofania', icon: 'apron-clothing', value: lightsSubsection.brakeLight, remark: lightsSubsection.brakeLightRemark },
    { id: 'brake', title: 'Stopu', icon: 'stop', value: lightsSubsection.reverseLight, remark: lightsSubsection.reverseLightRemark },
    { id: 'turnSignal', title: 'Kierunkowskazy', icon: 'arrows-horizontal', value: lightsSubsection.turnSignalLight, remark: lightsSubsection.turnSignalLightRemark },
  ];

  // Mapuj tylko te światła, które mają wartość
  cols.forEach(light => {
    if (!light.value) return;
    else {
      const item = createLightItem(light.id, light.title, light.icon, light.value, light.remark);
      assessmentItems.push(item);
    }
  });

  // Światła USA - zawsze dodaj 8. komórkę (pełna sekcja USA lub pusty placeholder)
  if (lightsSubsection.usaLights) {
    assessmentItems.push({
      id: 'usaLightsSection',
      title: 'Pojazdy z USA / Kanady',
      customBody: createUsaLightsCustomBody(lightsSubsection),
    });
  } else {
    assessmentItems.push({
      id: 'usaLightsPlaceholder',
      title: 'Dla pojazdów z USA /Kanady',
      customBody: (
        <div className="flex items-center justify-center w-full h-full py-8 md:py-0">
          <p className="text-xl font-semibold text-center text-zinc-300 leading-snug">
            Dla pojazdów<br />z USA /Kanady
          </p>
        </div>
      ),
    });
  }

  // Jeśli brak jakichkolwiek danych o światłach - ukryj sekcję
  if (assessmentItems.length === 0 && !lightsSubsection.expertSummary) {
    return null;
  }

  return {
    assessmentItems,
    usaLights: lightsSubsection.usaLights ?? false,
    expertSummary: lightsSubsection.expertSummary || null,
  };
};

export const mapCarBodySectionToUI = (
  section: PublicReportCarBodySectionDto | null | undefined,
): MappedCarBodySection | null => {
  if (!section) {
    return null;
  }

  const summary = section.reportSectionSummary;
  const rating = mapScoreToNumber(summary?.score);
  const light = mapCarLightsSubsection(section.carLightsSubsection);

  return {
    ratingSection: {
      title: 'Ocena kontroli nadwozia',
      rating,
      ratingMessage: rating !== null ? getRatingMessage(rating) : 'Brak oceny',
      description: summary?.remarks && summary?.remarks?.length > 0 ? "Zwróć uwagę na podsumowanie wybranych zastrzeżeń dotyczących tej sekcji." : null,
      defectFree: summary?.defectFree ?? false,
    },
    remarks: summary?.remarks || [],
    paintAndGapMeasurements: mapPaintThicknessAndGapMeasurements(
      section.paintThicknessAndGapMeasurementsSubsection,
    ),
    windowsData: section.carWindowsSubsection
      ? {
        reportSubsection: section.carWindowsSubsection.reportSubsection,
        fields: section.carWindowsSubsection.fields as CarWindowsData,
        expertSummary: section.carWindowsSubsection.expertSummary,
      }
      : null,
    tiresData: mapCarTiresSubsection(section.carTiresSubsection),
    lightsData: light,
  };
};
